import { useClientPortalAPI } from "@certn/api";
import { Text, Stack, Link } from "@certn/cascadia";
import {
  Avatar,
  ColumnDefinition,
  Container,
  CopyToClipboard,
  DataTable,
  Layout,
  Loader,
  MediaQuery,
  Pagination,
} from "@certn/cascadia-react";
import {
  orderingParamToSortDescriptor,
  sortDescriptorToOrderingParam,
} from "@certn/cascadia-react/components/data-tables/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { ActionsDropdown } from "./components/ActionsDropdown";
import { GroupFilter } from "./components/GroupFilter";
import { TableDataCard } from "./components/TableDataCard";
import { TagsFilter } from "./components/TagsFilter";
import { mapProfileResultsToRows } from "./utils";
import { ConfigurationError } from "../../components/ConfigurationError";
import { AppFooterLinks } from "../../components/app-footer/AppFooterLinks";
import { AppHeader } from "../../components/app-header/AppHeader";
import { AppNavigation } from "../../components/app-navigation/AppNavigation";
import { FilterBar } from "../../components/filters/FilterBar";
import { PageTitle } from "../../components/page-title/PageTitle";
import { usePermissions } from "../../components/permissions/PermissionsProvider";
import { TagList } from "../../components/tag-list/TagList";
import { useQueryParams } from "../../hooks/useQueryParams";
import { queryParamsToApiParams } from "../utils";

export function ProfilesListViewPage() {
  const { t } = useTranslation();
  const { params, setParam } = useQueryParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const { clientPortalCaseTags, clientPortalHideSearchBarOnPeoplePortal } =
    useFlags();

  const { data, isLoading } = useClientPortalAPI(
    `/api/internal/client-portal/persons/`,
    queryParamsToApiParams(params)
  );

  const rows = mapProfileResultsToRows(data?.results);

  const columns: ColumnDefinition<(typeof rows)[number]>[] = [
    {
      key: "name",
      name: t("Name"),
      isRowHeader: true,
      width: 956,
      testID: "name-column-header",
      render({ name, email }: { name: string; email: string }) {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Avatar name={name || email} />
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Text testID="profile-name">{name || "--"}</Text>
              <Text
                style="global/info-label"
                color="text-weak"
                testID="profile-email"
              >
                <CopyToClipboard text={email} testID="copyable-email" />
              </Text>
            </div>
          </div>
        );
      },
    },
    {
      key: "groups",
      name: t("Groups"),
      isRowHeader: true,
      width: 318,
      testID: "groups-column-header",
      render({ groups }: { groups: string }) {
        return (
          <Text style="form/body-secondary" testID="profile-groups">
            {groups}
          </Text>
        );
      },
    },
    clientPortalCaseTags && {
      key: "tags",
      name: t("Tags"),
      isRowHeader: true,
      width: 318,
      testID: "tags-column-header",
      render({ tags }: any) {
        return (
          <TagList
            tags={tags}
            capacity={6}
            hasOverflowDetails
            testID="profile-list"
          />
        );
      },
    },
    {
      key: "latest_case_modified",
      name: t("Last updated"),
      isRowHeader: true,
      width: 132,
      allowsSorting: true,
      testID: "last-updated-column-header",
    },
    {
      key: "actions",
      isAction: true,
      width: 48,
      render({ id }: { id: string }) {
        return <ActionsDropdown id={id} />;
      },
    },
  ].filter((item) => !!item);

  const userHasPermission =
    hasPermission({
      action: "read",
      target: "subject",
      scope: "all",
    }) &&
    (hasPermission({
      action: "read",
      target: "case",
      scope: "all",
    }) ||
      hasPermission({
        action: "read",
        target: "case",
        scope: "my_groups",
      }));

  if (!userHasPermission) {
    return (
      <ConfigurationError
        message={t(
          "Hmm, it looks like you do not have permission to use this feature."
        )}
      />
    );
  }

  return (
    <Layout
      layout="narrow"
      header={<AppHeader />}
      navigation={<AppNavigation />}
      footer={<AppFooterLinks />}
    >
      <Container layout="full-width">
        <PageTitle>{t("Profiles")}</PageTitle>
        <MediaQuery sm type="hide">
          <Stack orientation="horizontal" gap="space-2">
            <Text color="text-weak">
              <Trans>
                Profiles are a central hub for viewing cases associated with a
                single applicant, enabling easier applicant management.
              </Trans>
            </Text>
            <Link
              target="_blank"
              href="https://portal.certn.co/hc/en-us/articles/31203731069459-Applicant-profiles"
              textStyle="global/body-secondary"
              linkStyle="monochrome"
              testID="profiles-learn-more-link"
            >
              <Trans>Learn more</Trans>
            </Link>
          </Stack>
        </MediaQuery>

        <FilterBar
          hasSearch={clientPortalHideSearchBarOnPeoplePortal}
          testID="profiles"
        >
          <GroupFilter />
          <TagsFilter />
        </FilterBar>
        <MediaQuery sm type="hide">
          <DataTable
            columns={columns}
            rows={rows ? rows : []}
            aria-label={t("Profile Table")}
            testId="profile-list-view-data-table"
            onRowAction={(id) => {
              navigate(`/profiles/${id}`, {
                state: {
                  last: { href: `${location.pathname}${location.search}` },
                },
              });
            }}
            onSortChange={(sortDescriptor) =>
              setParam(
                "ordering",
                sortDescriptorToOrderingParam(sortDescriptor)
              )
            }
            sortDescriptor={orderingParamToSortDescriptor(params.ordering)}
            isLoading={isLoading}
          />
        </MediaQuery>
        <MediaQuery sm type="show">
          <Container layout="full-width">
            {!isLoading ? (
              rows &&
              rows.map((profile, i) => {
                return (
                  <TableDataCard
                    name={profile.name}
                    id={profile.id}
                    email={profile.email}
                    key={i}
                    groups={profile.groups}
                    tags={profile.tags}
                  />
                );
              })
            ) : (
              <Loader />
            )}
          </Container>
        </MediaQuery>

        <Pagination
          setPage={(page) => setParam("page", page.toString())}
          setPageCount={(pageCount) =>
            setParam("page_size", pageCount.toString())
          }
          total={data?.count ?? 0}
          page={params.page ? Number(params.page) : 1}
          pageCount={params["page_size"] ? Number(params["page_size"]) : 25}
        />
      </Container>
    </Layout>
  );
}
